import React, { useState, useRef, useEffect } from 'react';
import { FlexBoxHorizontal, FlexBoxVertical, FlexItem, FlexBox, Label, TranslateTypography, TextField } from '../../atoms';

export const StyleColorImgSub = ({ data, changeImg, orderData }) => {
  const [imgSrc, setImgSrc] = useState('');
  const imgRef = useRef(null);


  useEffect(() => {
    if (data?.img) {
      // 캐시 방지 쿼리 파라미터 추가
      setImgSrc(`${data.img}?timestamp=${new Date().getTime()}`);
    } else {
      setImgSrc('');
    }
  }, [data]); // data 변경 감지

  // 탭 컴포넌트
  const TabPanel = ({ children, value, index }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        style={{ width: '100%' }}
      >
        {value === index && children}
      </div>
    );
  };

  // 피벗 테이블 컴포넌트
  const PivotTable = ({ data }) => {
    // 데이터 가공 함수
    const processPivotData = (data) => {
      const pivotData = {};
      const sizes = new Set();
      const sizeTotals = {}; // 사이즈별 총계를 저장할 객체
      
      // 중복 제거를 위한 Map 사용
      const uniqueData = new Map();
      
      // 먼저 중복 데이터를 제거
      data.forEach(item => {
        const key = `${item.COLOR_CD}-${item.SIZE_CD}`;
        if (!uniqueData.has(key)) {
          uniqueData.set(key, item);
        }
      });
      
      // 중복이 제거된 데이터로 피벗 테이블 생성
      uniqueData.forEach(item => {
        const color = item.COLOR_CD;
        const size = item.SIZE_CD;
        const qty = item.ORD_QTY;
        
        sizes.add(size);
        
        if (!pivotData[color]) {
          pivotData[color] = {};
        }
        
        // 중복이 제거된 데이터이므로 단순 할당
        pivotData[color][size] = qty;
        
        // 사이즈별 총계 계산
        sizeTotals[size] = (sizeTotals[size] || 0) + qty;
      });

      return { 
        pivotData, 
        sizes: Array.from(sizes),
        sizeTotals
      };
    };

    const { pivotData, sizes, sizeTotals } = processPivotData(data);
    
    // 전체 총계 계산
    const grandTotal = Object.values(sizeTotals).reduce((sum, val) => sum + val, 0);

    return (
      <div style={{ overflow: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'auto' }}>
          <thead>
            <tr>
              <th style={{...styles.headerCell, width: '20%'}}></th>
              {sizes.map(size => (
                <th key={size} style={{...styles.headerCell, width: `${80 / (sizes.length + 1)}%`}}>{size}</th>
              ))}
              <th style={{...styles.headerCell, width: `${80 / (sizes.length + 1)}%`, backgroundColor: '#f5f5f5'}}>합계</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(pivotData).map(([color, sizeData]) => {
              const rowTotal = Object.values(sizeData).reduce((sum, val) => sum + val, 0);
              return (
                <tr key={color}>
                  <td style={{...styles.cell, width: '20%'}}>{color}</td>
                  {sizes.map(size => (
                    <td key={size} style={{...styles.cell, width: `${80 / (sizes.length + 1)}%`}}>
                      {(sizeData[size] || 0).toLocaleString()}
                    </td>
                  ))}
                  <td style={{...styles.cell, width: `${80 / (sizes.length + 1)}%`, backgroundColor: '#f5f5f5'}}>
                    {rowTotal.toLocaleString()}
                  </td>
                </tr>
              );
            })}
            {/* 총계 행 추가 */}
            <tr>
              <td style={{...styles.totalCell, width: '20%'}}>합계</td>
              {sizes.map(size => (
                <td key={size} style={{...styles.totalCell, width: `${80 / (sizes.length + 1)}%`}}>
                  {(sizeTotals[size] || 0).toLocaleString()}
                </td>
              ))}
              <td style={{...styles.totalCell, width: `${80 / (sizes.length + 1)}%`, backgroundColor: '#f5f5f5'}}>
                {grandTotal.toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  // 국가별 피벗 테이블 컴포넌트
  const CountryPivotTables = ({ data }) => {
    const [selectedTab, setSelectedTab] = useState(0);
    
    // 국가별로 데이터 그룹화
    const countryGroups = data.reduce((acc, item) => {
      const country = item.PO_CNTRY;
      if (!acc[country]) {
        acc[country] = [];
      }
      acc[country].push(item);
      return acc;
    }, {});

    const countries = Object.keys(countryGroups);

    return (
      <div style={{ width: '100%' }}>
        {/* 탭 버튼 */}
        <div style={styles.tabContainer}>
          {countries.map((country, index) => (
            <button
              key={country}
              onClick={() => setSelectedTab(index)}
              style={{
                ...styles.tabButton,
                backgroundColor: selectedTab === index ? '#6161e6' : 'white',
                color: selectedTab === index ? 'white' : 'black'
              }}
            >
              {country}
            </button>
          ))}
        </div>

        {/* 탭 컨텐츠 */}
        {countries.map((country, index) => (
          <TabPanel key={country} value={selectedTab} index={index}>
            <PivotTable data={countryGroups[country]} />
          </TabPanel>
        ))}
      </div>
    );
  };

  // 스타일 정의
  const styles = {
    headerCell: {
      border: '1px solid #ddd',
      padding: '8px',
      backgroundColor: '#f5f5f5',
      textAlign: 'center',
      fontSize: '0.8rem',
      fontWeight: '400 !important',
      minWidth: '40px'
    },
    cell: {
      border: '1px solid #ddd',
      padding: '8px',
      textAlign: 'center',
      fontSize: '0.8rem',
      minWidth: '40px'
    },
    tabContainer: {
      display: 'flex',
      gap: '4px',
      marginBottom: '5px',
      marginTop: '10px'
    },
    tabButton: {
      padding: '8px 16px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    totalCell: {
      border: '1px solid #ddd',
      padding: '8px',
      textAlign: 'center',
      fontSize: '0.8rem',
      backgroundColor: '#f5f5f5',
    }
  };

  return (
    <FlexBoxVertical style={{justifyContent: 'flex-start', height: '90%'}}>
      {/* 상단부: 코드 및 브랜드 */}
      <FlexBoxHorizontal style={{ height: '40px' }}>
        <Label style={{ width: '45%', margin: '3px', backgroundColor: 'lavender', color: '#6161e6', fontWeight: 'bold'  }}>
            {data && data['part_cd'] ? data['part_cd'] : '품번'}
        </Label>
        <Label style={{ width: '45%', margin: '3px', backgroundColor: 'lavender', color: '#6161e6', fontWeight: 'bold' }}>
            {data && data['prdt_nm'] ? data['prdt_nm'] : '제품명'}
        </Label>
      </FlexBoxHorizontal>

      {/* description */}
      

      {/* 상세정보 */}
      {/* <FlexBoxVertical style={{height: '100%'}}> */}
        <FlexBoxHorizontal style={{height: 'auto', justifyContent: 'space-between', width: '90%'}}>
        <TextField
            defaultValue={data && data.hasOwnProperty('fit') && data['fit'] !== null  ? data['fit'] : ' '}
            placeholder="핏"
            readOnly
            width={'180px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('tag_price') && data['tag_price'] !== null  ? data['tag_price'].toLocaleString() : ' '}
            placeholder="확정택가"
            readOnly
            width={'180px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('tag_price_chn') && data['tag_price_chn'] !== null  ? data['tag_price_chn'].toLocaleString() : ' '}
            placeholder="확정중국택가"
            readOnly
            width={'180px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('ord_qty') && data['ord_qty'] !== null  ? data['ord_qty'].toLocaleString() : ' '}
            placeholder="발주수량"
            readOnly
            width={'180px'}
          />
        </FlexBoxHorizontal>
        {/* 이미지 박스 */}
        <FlexBox
          style={{
            width: '100%',
            height: '30%',
            position: 'relative',
            overflow: 'hidden',
            margin: '10px'
          }}
        >
          <img
          ref={imgRef}
          src={imgSrc}
          alt="Content"
          onError={(e) => {
            e.target.src = 'https://img.icons8.com/?size=100&id=IuQpbmYpl7VN&format=png&color=dadee0';
          }}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
        </FlexBox>
        <FlexBoxHorizontal style={{height: 'auto', justifyContent: 'space-between', width: '90%' }}>
        <TextField
            defaultValue={data && data.hasOwnProperty('dsgnr_nm') && data['dsgnr_nm'] !== null   ? data['dsgnr_nm'] : ' '}
            placeholder="디자이너"
            readOnly
            width={'200px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('dsgnr_plan_nm') && data['dsgnr_plan_nm'] !== null   ? data['dsgnr_plan_nm'] : ' '}
            placeholder="MD"
            readOnly
            width={'200px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('dsgnr_goods_nm') && data['dsgnr_goods_nm'] !== null   ? data['dsgnr_goods_nm'] : ' '}
            placeholder="소싱 담당"
            readOnly
            width={'200px'}
          />
          <TextField
            defaultValue={data && data.hasOwnProperty('mfac_compy_nm') && data['mfac_compy_nm'] !== null   ? data['mfac_compy_nm'] : ' '}
            placeholder="협력사"
            readOnly
            width={'200px'}
          />
        </FlexBoxHorizontal>
        <FlexBox style={{height: 'auto', justifyContent: 'space-between', width: '90%', margin: '5px'}}>
        <TextField
            defaultValue={data && data.hasOwnProperty('color_all') && data['color_all'] !== null ? data['color_all'] : ' '}
            placeholder="컬러 ALL"
            readOnly
            width={'100%'}
          />
        </FlexBox>
        <div style={{ width: '90%', margin: '5px', overflow: 'auto' }}>
        {orderData && orderData.length > 0 ? (
          <CountryPivotTables data={orderData} />
        ) : (
          <></>
        )}
        </div>
      {/* </FlexBoxVertical> */}
    </FlexBoxVertical>
  );
};

